import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { debounce } from '@theme/utilities';
import { morph } from '@theme/morph';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  #controller = new AbortController();
  // Wrap in a lambda because `this.updatePrice` is a class field declared
  // further down — class fields evaluate in declaration order, so a direct
  // reference here would throw "Cannot read properties of undefined".
  // The lambda defers the lookup until the debounce actually fires.
  #debouncedUpdatePrice = debounce((event) => this.updatePrice(event), 100);

  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.#debouncedUpdatePrice, {
      signal: this.#controller.signal
    });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * Updates the price with minimal DOM changes using morph.
   * Debounced to prevent rapid updates from causing lag on swatch changes.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newPrice = event.detail.data.html.querySelector('product-price [ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (!newPrice || !currentPrice) return;

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      // Use morph instead of replaceWith for surgical DOM updates
      // This prevents the animation stutters on Safari when price changes
      morph(currentPrice, newPrice);
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
